<template>
    <!-- Your scorecard numbers still to enter for each team's due week (usually the week just finished; myNumbers.ts),
         entered right here (API scoreboard.ts GET /mine, POST /values). On Employee Home it's hidden when you own no
         metrics, or once they're all in (after a save here it says so instead). With popup set it's the header's Numbers
         quick add (App.vue). -->
    <div :class="popup ? 'error-msg-wrap' : ''">
        <div :class="popup ? 'form-wrapper slide-up home-numbers-popup' : ''">
            <div v-if="popup" class="form-close">
                <button type="button" class="form-close-x" title="Close" @click="$emit('close')">&times;</button>
            </div>
            <p v-if="popup && !numbersLoaded" class="home-numbers-loading">Loading your numbers&hellip;</p>
            <div v-if="numbersLoaded && (popup || missing.length || numbersSaved)" class="home-numbers">
                <div class="home-numbers-head">
                    <div class="home-numbers-icon"><svg-icon icon="chart" /></div>
                    <div class="home-numbers-title">
                        <b>Your Numbers{{ headerWeek ? ' for ' + titleCase(relativeWeekLabel(headerWeek)) : '' }}</b>
                        <span><template v-if="headerWeek">{{ weekRangeNumbered(headerWeek) }}</template><template v-if="missing.length">{{ headerWeek ? ' · ' : '' }}{{ missing.length }} still to enter</template></span>
                    </div>
                    <a class="home-numbers-open" @click.prevent="openScorecard()">Open Scorecard</a>
                </div>
                <template v-if="missing.length">
                    <div v-for="m in missing" :key="m.ID" class="home-numbers-row">
                        <div class="home-numbers-l">
                            <b>{{ m.Name }}</b>
                            <span><template v-if="!headerWeek">{{ relativeWeekLabel(dueWeekFor(m)) }} · </template>{{ teamLabel(m.Team) }} · goal {{ goalText(m, dueWeekFor(m)) }} · week before {{ hasValue(m, addDays(dueWeekFor(m), -7)) ? formatValue(m, m.Values[addDays(dueWeekFor(m), -7)]) : '—' }} · due {{ dueText(m.DueDay, m.DueTime) }}</span>
                        </div>
                        <div class="home-numbers-in" :class="inputClass(m)">
                            <input type="number" step="any" v-model="numbers[m.ID]" placeholder="—" :aria-label="m.Name" @keyup.enter="saveNumbers()" />
                            <em v-if="m.Unit">{{ m.Unit }}</em>
                        </div>
                    </div>
                    <div class="home-numbers-actions">
                        <span class="home-numbers-hint">Green means on goal. Leave any you don't have yet.</span>
                        <button type="button" class="btn" :class="savingNumbers ? 'disable-button' : ''" @click="saveNumbers()">Save Numbers</button>
                    </div>
                </template>
                <div v-else class="home-numbers-done"><svg-icon icon="check" />All your numbers are in{{ headerWeek ? ' for ' + relativeWeekLabel(headerWeek).toLowerCase() : '' }}.</div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import SvgIcon from './SvgIcon.vue';
import { addDays, weekRangeNumbered, relativeWeekLabel, goalText, formatValue, passes, dueText } from '@/scoreboard';
import { dueWeekFor, hasValue, missingNumbers, loadMyMetrics } from '@/myNumbers';

export default defineComponent({
    name: 'MyNumbers',
    props: {
        popup: { type: Boolean, default: false }
    },
    emits: [ 'close' ],
    components: {
        SvgIcon
    },
    data() {
        return {
            myMetrics: [] as any[], // the scorecard metrics you own, with the last three weeks' numbers
            numbers: {} as any, // what's typed in, by metric ID
            numbersLoaded: false,
            numbersSaved: false,
            savingNumbers: false
        }
    },
    created() {
        this.loadNumbers();
    },
    computed: {
        missing(): any[] {
            return missingNumbers(this.myMetrics);
        },
        // The week the card is about, when it's the same for every metric listed (else each row says its own)
        headerWeek(): string | null {
            const weeks = [ ...new Set((this.missing.length ? this.missing : this.myMetrics).map((m: any) => dueWeekFor(m))) ];
            return weeks.length == 1 ? weeks[0] as string : null;
        }
    },
    watch: {
        // Numbers entered somewhere else (the header's popup over Employee Home, the Scoreboard): catch up
        '$store.state.numbersToEnter'(count: number) {
            if(this.numbersLoaded && count != this.missing.length) {
                this.loadNumbers();
            }
        }
    },
    methods: {
        weekRangeNumbered,
        relativeWeekLabel,
        addDays,
        dueWeekFor,
        hasValue,
        goalText,
        formatValue,
        dueText,
        titleCase(text: string): string {
            return text.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        },
        async loadNumbers() {
            try {
                this.myMetrics = await loadMyMetrics();
            } catch(e) {
                console.log(e);
            }
            this.numbersLoaded = true;
        },
        inputClass(m: any): string {
            const p = passes(m, this.numbers[m.ID], dueWeekFor(m));
            return p === null ? '' : p ? 'pass' : 'fail';
        },
        openScorecard() {
            this.$emit('close');
            this.$router.push('/scoreboard?mine=1');
        },
        // Saves the ones filled in; the rest stay listed
        async saveNumbers() {
            if(this.savingNumbers) {
                return;
            }
            const entries = this.missing
                .filter((m: any) => this.numbers[m.ID] !== undefined && String(this.numbers[m.ID]).trim() !== '')
                .map((m: any) => ({ MetricID: m.ID, Week: dueWeekFor(m), Value: this.numbers[m.ID] }));
            if(!entries.length) {
                alert('Enter at least one number to save.');
                return;
            }
            this.savingNumbers = true;
            try {
                await axios.post(`${this.$store.state.apiUrl}/api/scoreboard/values`, { Values: entries });
                for(const e of entries) {
                    const m = this.myMetrics.find((x: any) => x.ID == e.MetricID);
                    if(m) {
                        m.Values = { ...m.Values, [e.Week]: Number(e.Value) };
                    }
                    delete this.numbers[e.MetricID];
                }
                this.numbersSaved = true;
                this.$store.state.numbersToEnter = this.missing.length;
            } catch(e) {
                console.log(e);
                alert('There was an error saving these numbers');
            }
            this.savingNumbers = false;
        }
    }
});
</script>
